import type { ChatbotEntity } from '@/services/chatbot-service/models'

export interface MentionMatch {
  query: string
  start: number // index of the @ character in the string
}

/**
 * Detects an active @mention query before the cursor position.
 * The @ must be at the start of the string or preceded by whitespace.
 * Returns null when the cursor is not inside an active mention.
 */
export function getMentionQuery(value: string, cursorPos: number): MentionMatch | null {
  const before = value.slice(0, cursorPos)
  const atIndex = before.lastIndexOf('@')
  if (atIndex === -1) return null
  if (atIndex > 0 && !/\s/.test(before[atIndex - 1])) return null

  const query = before.slice(atIndex + 1)
  // A newline after @ means the user left the mention context
  if (query.includes('\n')) return null

  return { query, start: atIndex }
}

/** Resolves @EntityName mentions to their <vehicle|group id="..." /> tag form. Longest-name-first to avoid partial substitutions. */
export function resolveMentions(message: string, entities: ChatbotEntity[]): string {
  const sorted = [...entities].sort((a, b) => b.name.length - a.name.length)
  let result = message

  for (const entity of sorted) {
    const escaped = entity.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // \b ensures we don't match a name that is a prefix of a longer word
    const regex = new RegExp(`@${escaped}\\b`, 'gi')
    result = result.replace(regex, `<${entity.type} id="${entity.id}" />`)
  }

  return result
}
